import { ColorClubMatas1, ColorClubMatasPlus1, Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import type { BasketViewModel } from 'autogen/swagger/all';
import {
    NoLinesInBasket,
    BasketHeaderPricePrefix,
    PageName,
    PayWithPoints,
    ContinueShopping
} from 'autogen/translation-keys/trans-website-basket';
import { Button } from 'components/Button';
import Flex from 'components/Layout/components/Flex';
import { Typography } from 'components/generic/typography';
import toHtml from 'helpers/translations';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import { format } from 'modules/helpers/strings';
import useIsClubMatasMember from 'hooks/user/useIsClubMatasMember';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import CheckoutActions from '../BasketCheckoutActions/CheckoutActions';

/* #region styles */
const StyledButton = styled(Button)`
    flex: 1;
    white-space: nowrap;
`;

const ActionContainer = styled(Flex.Row)`
    min-width: auto;
    ${breakpoint.up(sizes.sm)} {
        min-width: 280px;
    }
`;

const PointText = styled(Typography.Body)<{ $isMatasPlus: boolean }>`
    font-style: italic;

    & > b {
        color: ${({ $isMatasPlus }) => ($isMatasPlus ? ColorClubMatasPlus1 : ColorClubMatas1)};
    }
`;

const BasketTotal = styled(Typography.Body)`
    ${breakpoint.up(sizes.sm)} {
        display: none;
    }
`;
/* #endregion */

export default function BasketHeader({
    model: {
        basketContent: {
            hideHeadline,
            hideContinueShoppingButton,
            basket: {
                isEmpty,
                basketFooter: {
                    basketSummary: { total }
                },
                hasSubscriptions
            },
            currentPoints,
            isPrizeCertificatesEnabled,
            checkoutButton
        }
    }
}: {
    model: BasketViewModel;
}) {
    const { t } = useTranslation();
    const { isMatasPlusMember } = useIsClubMatasMember();
    const isAuthenticated = useIsAuthenticated();

    const shouldShowPayWithPointText =
        isAuthenticated && currentPoints > 0 && isPrizeCertificatesEnabled && !hasSubscriptions;

    if (isEmpty) {
        return (
            <Flex direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                <Flex.Column>
                    <Flex direction="column" justifyContent="space-between" alignItems="start" gap={Spacing4}>
                        {!hideHeadline && <Typography.Headline size="L">{t(PageName)}</Typography.Headline>}
                        <Typography.Body as="span">{t(NoLinesInBasket)}</Typography.Body>
                    </Flex>
                </Flex.Column>

                {!hideContinueShoppingButton && (
                    <ActionContainer gap={Spacing8}>
                        <StyledButton forwardedAs="a" href="/" size="lg" variant="secondary">
                            {t(ContinueShopping)}
                        </StyledButton>
                    </ActionContainer>
                )}
            </Flex>
        );
    }

    return (
        <Flex direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
            <Flex.Column>
                <Flex direction="column" justifyContent="space-between" alignItems="start" gap={Spacing4}>
                    <Typography.Headline size="L">{t(PageName)}</Typography.Headline>

                    <BasketTotal forwardedAs="span" size="M" strong>{`${t(BasketHeaderPricePrefix)} ${
                        total.total
                    }`}</BasketTotal>
                </Flex>

                {shouldShowPayWithPointText && (
                    <PointText forwardedAs="span" $isMatasPlus={isMatasPlusMember}>
                        {toHtml(format(t(PayWithPoints))(currentPoints))}
                    </PointText>
                )}
            </Flex.Column>

            <CheckoutActions model={checkoutButton} />
        </Flex>
    );
}
